import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Bell, CheckCircle2, Clock, User,
  AlertTriangle, Package, ChevronDown, ChevronUp, ShoppingCart, Warehouse
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { cn } from "../../lib/utils";

const API = process.env.REACT_APP_BACKEND_URL;

const AvvisiScorte = () => {
  const navigate = useNavigate();
  const { user, token, localeCorrente, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tipo, setTipo] = useState("food");
  const [filtro, setFiltro] = useState("tutti");
  const [search, setSearch] = useState("");
  const [prodotti, setProdotti] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(true);
  const [canRiordinare, setCanRiordinare] = useState(false);

  // Dialog segnalazione
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProdotto, setSelectedProdotto] = useState(null);
  const [urgenza, setUrgenza] = useState("in_esaurimento");
  const [note, setNote] = useState("");
  const [giaRiordinato, setGiaRiordinato] = useState(false);
  const [saving, setSaving] = useState(false);

  // Dialog conferma risoluzione
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveAlertId, setResolveAlertId] = useState(null);
  const [resolveAlertNome, setResolveAlertNome] = useState("");
  const [resolveTipo, setResolveTipo] = useState(null);
  const [resolving, setResolving] = useState(false);

  // Check permessi riordino
  useEffect(() => {
    if (!token) return;
    if (isAdmin) { setCanRiordinare(true); return; }
    axios.get(`${API}/api/alert-scorte/can-riordinare`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setCanRiordinare(res.data.can_riordinare)).catch(() => {});
  }, [token, isAdmin]);

  const fetchAlerts = useCallback(async () => {
    if (!localeCorrente?.id || !token) return;
    try {
      const params = new URLSearchParams({ locale_id: localeCorrente.id, tipo });
      if (filtro !== "tutti") params.append("stato", filtro);
      const res = await axios.get(`${API}/api/alert-scorte/tutti?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlerts(res.data);
    } catch (err) { console.error("Errore fetch alerts:", err); }
  }, [localeCorrente?.id, token, tipo, filtro]);

  const fetchProdotti = useCallback(async () => {
    if (!localeCorrente?.id || !token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ locale_id: localeCorrente.id, tipo });
      if (search) params.append("search", search);
      const res = await axios.get(`${API}/api/alert-scorte/prodotti?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProdotti(res.data);
    } catch { toast.error("Errore nel caricamento prodotti"); }
    finally { setLoading(false); }
  }, [localeCorrente?.id, token, tipo, search]);

  useEffect(() => { fetchAlerts(); fetchProdotti(); }, [fetchAlerts, fetchProdotti]);

  const openSegnala = (prodotto) => {
    setSelectedProdotto(prodotto);
    setUrgenza("in_esaurimento");
    setNote("");
    setGiaRiordinato(false);
    setDialogOpen(true);
  };

  const handleSegnala = async () => {
    if (!selectedProdotto) return;
    setSaving(true);
    try {
      const params = new URLSearchParams({ user_id: user.id, user_nome: user.nome || user.email || user.telefono });
      await axios.post(`${API}/api/alert-scorte?${params}`, {
        tipo, prodotto_id: selectedProdotto.id, urgenza, note: note || null,
        locale_id: localeCorrente.id, gia_riordinato: giaRiordinato,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(giaRiordinato ? "Segnalato e riordinato!" : "Segnalazione inviata!");
      setDialogOpen(false);
      fetchAlerts();
      fetchProdotti();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore nella segnalazione"); }
    finally { setSaving(false); }
  };

  const openResolveDialog = (alertId, prodottoNome, tipo) => {
    setResolveAlertId(alertId);
    setResolveAlertNome(prodottoNome);
    setResolveTipo(tipo);
    setResolveDialogOpen(true);
  };

  const handleRisolvi = async () => {
    if (!resolveAlertId || !resolveTipo) return;
    setResolving(true);
    try {
      const params = new URLSearchParams({ user_id: user.id, user_nome: user.nome || user.email || user.telefono });
      await axios.put(`${API}/api/alert-scorte/${resolveAlertId}/risolvi?${params}`, {
        tipo_risoluzione: resolveTipo,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(resolveTipo === "rifornito_magazzino" ? "Segnato come rifornito dal magazzino!" : "Segnato come riordinato dal fornitore!");
      setResolveDialogOpen(false);
      fetchAlerts();
      fetchProdotti();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setResolving(false); }
  };

  const formatDataRelativa = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return "adesso";
    if (diffMins < 60) return `${diffMins} min fa`;
    if (diffHours < 24) return `${diffHours}h fa`;
    if (diffDays === 1) return "ieri";
    if (diffDays < 7) return `${diffDays} giorni fa`;
    return date.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
  };

  const alertAttivi = alerts.filter(a => !a.risolto);
  const alertRisolti = alerts.filter(a => a.risolto);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b">
        <div className="flex items-center gap-3 p-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft className="h-5 w-5" /></Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold">Avvisi Scorte</h1>
            <p className="text-xs text-muted-foreground">Segnala e monitora riordini</p>
          </div>
        </div>

        <div className="flex gap-2 px-4 pb-3">
          <Button variant={tipo === "food" ? "default" : "outline"}
            className={cn("flex-1", tipo === "food" && "bg-emerald-600 hover:bg-emerald-700")}
            onClick={() => setTipo("food")}>
            <span className="mr-2">🥗</span> Food & Beverage
          </Button>
          <Button variant={tipo === "materiali" ? "default" : "outline"}
            className={cn("flex-1", tipo === "materiali" && "bg-amber-600 hover:bg-amber-700")}
            onClick={() => setTipo("materiali")}>
            <span className="mr-2">📦</span> Attrezz. & Packaging
          </Button>
        </div>

        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Cerca prodotto..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Sezione Alert */}
        <div className="bg-card rounded-lg border overflow-hidden">
          <button className="w-full flex items-center justify-between p-3 hover:bg-muted/50" onClick={() => setShowAlerts(!showAlerts)}>
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-amber-500" />
              <span className="font-medium">Segnalazioni</span>
              {alertAttivi.length > 0 && <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">{alertAttivi.length} da risolvere</span>}
              {alertRisolti.length > 0 && <span className="bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full">{alertRisolti.length} risolti</span>}
            </div>
            {showAlerts ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {showAlerts && (
            <div className="border-t">
              <div className="flex gap-1 p-2 bg-muted/30">
                {[{ key: "tutti", label: "Tutti" }, { key: "attivi", label: "Da risolvere" }, { key: "risolti", label: "Risolti" }].map(f => (
                  <button key={f.key} onClick={() => setFiltro(f.key)}
                    className={cn("px-3 py-1 text-xs rounded-full transition-colors",
                      filtro === f.key ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted")}>
                    {f.label}
                  </button>
                ))}
              </div>

              <div className="max-h-[300px] overflow-y-auto divide-y">
                {alerts.length === 0 ? (
                  <p className="text-center py-6 text-muted-foreground text-sm">Nessuna segnalazione</p>
                ) : alerts.map(alert => (
                  <div key={alert.id} className={cn("p-3",
                    alert.risolto
                      ? alert.tipo_risoluzione === "rifornito_magazzino"
                        ? "bg-teal-50 dark:bg-teal-950/20"
                        : "bg-indigo-50 dark:bg-indigo-950/20"
                      : "bg-amber-50 dark:bg-amber-950/20"
                  )} data-testid={`alert-scorta-${alert.id}`}>
                    <div className="flex items-start gap-2">
                      <span className="text-lg">{alert.urgenza_info?.icona || "🟡"}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{alert.prodotto_nome}</p>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                          <User className="h-3 w-3" />
                          <span>{alert.creato_da_nome === user?.nome || alert.creato_da_nome === (user?.email || user?.telefono) ? "Tu" : alert.creato_da_nome}</span>
                          <span>•</span>
                          <Clock className="h-3 w-3" />
                          <span>{formatDataRelativa(alert.created_at)}</span>
                        </div>

                        {alert.risolto ? (
                          <div className="flex items-center gap-1 text-xs mt-1">
                            {alert.tipo_risoluzione === "rifornito_magazzino" ? (
                              <>
                                <Warehouse className="h-3 w-3 text-teal-600" />
                                <span className="text-teal-600 font-medium">Rifornito dal magazzino</span>
                              </>
                            ) : (
                              <>
                                <ShoppingCart className="h-3 w-3 text-indigo-600" />
                                <span className="text-indigo-600 font-medium">Riordinato dal fornitore</span>
                              </>
                            )}
                            <span className="text-muted-foreground">da {alert.risolto_da_nome === user?.nome || alert.risolto_da_nome === (user?.email || user?.telefono) ? "te" : alert.risolto_da_nome}</span>
                            <span className="text-muted-foreground">•</span>
                            <span className="text-muted-foreground">{formatDataRelativa(alert.risolto_at)}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-xs text-amber-600 mt-1">
                            <Clock className="h-3 w-3" />
                            <span>In attesa di risoluzione</span>
                          </div>
                        )}

                        {alert.note && <p className="text-xs text-muted-foreground mt-1 italic">"{alert.note}"</p>}
                        {alert.note_risoluzione && <p className="text-xs text-muted-foreground mt-0.5 italic">Nota: "{alert.note_risoluzione}"</p>}

                        {/* Bottoni risoluzione - solo per chi ha permesso */}
                        {!alert.risolto && canRiordinare && (
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={() => openResolveDialog(alert.id, alert.prodotto_nome, "rifornito_magazzino")}
                              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 active:scale-[0.97] transition-all"
                              data-testid={`btn-rifornito-${alert.id}`}>
                              <Warehouse className="h-4 w-4" />
                              Rifornito da magazzino
                            </button>
                            <button
                              onClick={() => openResolveDialog(alert.id, alert.prodotto_nome, "riordinato")}
                              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 active:scale-[0.97] transition-all"
                              data-testid={`btn-riordina-${alert.id}`}>
                              <ShoppingCart className="h-4 w-4" />
                              Riordinato da fornitore
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Lista prodotti */}
        <div>
          <h2 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
            <Package className="h-4 w-4" /> Tutti i prodotti
          </h2>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
          ) : prodotti.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {search ? "Nessun prodotto trovato" : "Nessun prodotto disponibile"}
            </div>
          ) : (
            <div className="space-y-1">
              {prodotti.map(prodotto => (
                <div key={prodotto.id}
                  className={cn("flex items-center justify-between p-3 bg-card rounded-lg border",
                    prodotto.ha_alert && "border-amber-300 bg-amber-50/50 dark:bg-amber-950/20")}>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="text-lg">{prodotto.categoria_icona}</span>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{prodotto.nome}</p>
                      {prodotto.categoria_nome && <p className="text-xs text-muted-foreground truncate">{prodotto.categoria_nome}</p>}
                    </div>
                  </div>
                  {prodotto.ha_alert ? (
                    <div className="flex items-center gap-1 text-xs text-amber-600 bg-amber-100 dark:bg-amber-900/50 px-2 py-1 rounded">
                      <Bell className="h-3 w-3" /><span>Segnalato</span>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" className="text-amber-600 border-amber-300 hover:bg-amber-50" onClick={() => openSegnala(prodotto)}>
                      <AlertTriangle className="h-4 w-4 mr-1" /> Segnala
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dialog Segnalazione */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" /> Segnala Riordino
            </DialogTitle>
          </DialogHeader>
          {selectedProdotto && (
            <div className="space-y-4">
              <div className="bg-muted/50 p-3 rounded-lg">
                <p className="font-medium">{selectedProdotto.nome}</p>
                {selectedProdotto.categoria_nome && <p className="text-sm text-muted-foreground">{selectedProdotto.categoria_nome}</p>}
              </div>

              <div>
                <Label className="text-sm">Urgenza</Label>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => setUrgenza("in_esaurimento")}
                    className={cn("flex-1 p-3 rounded-lg border-2 text-center transition-colors",
                      urgenza === "in_esaurimento" ? "border-yellow-500 bg-yellow-50 dark:bg-yellow-950/30" : "border-muted hover:border-yellow-300")}>
                    <span className="text-2xl block mb-1">🟡</span><span className="text-sm">In esaurimento</span>
                  </button>
                  <button onClick={() => setUrgenza("urgente")}
                    className={cn("flex-1 p-3 rounded-lg border-2 text-center transition-colors",
                      urgenza === "urgente" ? "border-red-500 bg-red-50 dark:bg-red-950/30" : "border-muted hover:border-red-300")}>
                    <span className="text-2xl block mb-1">🔴</span><span className="text-sm">Urgente</span>
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-sm">Note (opzionale)</Label>
                <Textarea placeholder="Es: Rimaste solo 2 scatole..." value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" rows={2} />
              </div>

              {/* Checkbox "Ho già riordinato" - solo per chi può */}
              {canRiordinare && (
                <label className="flex items-center gap-3 p-3 bg-emerald-50 dark:bg-emerald-950/20 rounded-lg border border-emerald-200 dark:border-emerald-800 cursor-pointer" data-testid="checkbox-gia-riordinato">
                  <input type="checkbox" checked={giaRiordinato} onChange={(e) => setGiaRiordinato(e.target.checked)}
                    className="w-5 h-5 rounded accent-emerald-600" />
                  <div>
                    <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5">
                      <ShoppingCart className="h-4 w-4" /> Ho già riordinato
                    </p>
                    <p className="text-xs text-emerald-600/70 dark:text-emerald-500/70">Segna subito come riordinato</p>
                  </div>
                </label>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSegnala} disabled={saving}
              className={giaRiordinato ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"}>
              {saving ? "Invio..." : giaRiordinato ? "Segnala & Riordina" : "Segnala"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog Conferma Risoluzione */}
      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              {resolveTipo === "rifornito_magazzino" ? (
                <><Warehouse className="h-5 w-5 text-teal-600" /> Conferma Rifornimento</>
              ) : (
                <><ShoppingCart className="h-5 w-5 text-indigo-600" /> Conferma Riordino</>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <div className={cn("p-4 rounded-lg border-2 text-center",
              resolveTipo === "rifornito_magazzino"
                ? "border-teal-200 bg-teal-50 dark:bg-teal-950/20 dark:border-teal-800"
                : "border-indigo-200 bg-indigo-50 dark:bg-indigo-950/20 dark:border-indigo-800"
            )}>
              <p className="font-semibold text-lg mb-1">{resolveAlertNome}</p>
              <p className="text-sm text-muted-foreground">
                {resolveTipo === "rifornito_magazzino"
                  ? "Il prodotto era già disponibile in magazzino e non necessita di un ordine al fornitore."
                  : "È stato effettuato un ordine al fornitore per il rifornimento di questo prodotto."}
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setResolveDialogOpen(false)} className="flex-1" data-testid="btn-annulla-risoluzione">Annulla</Button>
            <Button onClick={handleRisolvi} disabled={resolving}
              className={cn("flex-1", resolveTipo === "rifornito_magazzino"
                ? "bg-teal-600 hover:bg-teal-700"
                : "bg-indigo-600 hover:bg-indigo-700"
              )}
              data-testid="btn-conferma-risoluzione">
              {resolving ? "Salvataggio..." : "Conferma"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AvvisiScorte;
